import React, { useState } from 'react';
import { 
  IconButton, 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  Button, 
  TextField, 
  Box, 
  Typography, 
  Alert,
  Chip,
  Tooltip
} from '@mui/material';
import {
  EventNote as RescheduleIcon,
  Close as CloseIcon,
  Save as SaveIcon,
  Block as BlockIcon
} from '@mui/icons-material';
import AppointmentService from '../services/appointmentTimelineService';

// ===== FUNCIONES DE VALIDACIÓN =====

// Estados permitidos para hacer reschedule
const RESCHEDULE_ALLOWED_STATUSES = ['pending', 'reschedule'];

// Función para verificar si se puede hacer reschedule
const canRescheduleAppointment = (appointment) => {
  const status = appointment?.status?.toLowerCase();
  return RESCHEDULE_ALLOWED_STATUSES.includes(status);
};

// Validar que una cita tenga los campos requeridos para reschedule
const validateAppointmentForReschedule = (appointment) => {
  const requiredFields = ['userid', 'meetinguserid'];
  const missingFields = [];
  
  requiredFields.forEach(field => {
    // Verificar múltiples variaciones de nombres de campos
    const hasField = appointment[field] || 
                    appointment[field.replace('userid', 'userId')] ||
                    appointment[field.replace('userid', 'patientId')] ||
                    appointment[field.replace('meetinguserid', 'doctorId')] ||
                    appointment[field.replace('meetinguserid', 'meetingUserId')];
    
    if (!hasField) {
      missingFields.push(field);
    }
  });
  
  return {
    isValid: missingFields.length === 0,
    missingFields
  };
};

// Función auxiliar para agregar una hora a un tiempo
const addOneHourToTime = (timeString) => {
  const [hours, minutes] = timeString.split(':').map(Number);
  const newHours = (hours + 1) % 24;
  return `${newHours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
};

// ✅ FUNCIÓN CORREGIDA PARA FORMATEAR FECHA
// La entrada del input de fecha está en formato YYYY-MM-DD
// El backend espera una fecha válida que pueda ser parseada por new Date()
const formatDateForBackend = (dateString) => {
  if (!dateString) return '';
  
  // Si viene en formato YYYY-MM-DD del input
  if (dateString.includes('-') && dateString.split('-').length === 3) {
    const [year, month, day] = dateString.split('-');
    
    // Validar que sean números válidos
    if (!year || !month || !day || 
        year.length !== 4 || 
        parseInt(month) < 1 || parseInt(month) > 12 ||
        parseInt(day) < 1 || parseInt(day) > 31) {
      console.error('Invalid date format:', dateString);
      return '';
    }
    
    // Crear un objeto Date válido y retornar en formato ISO
    // O simplemente usar el formato YYYY-MM-DD que es estándar ISO
    return dateString; // YYYY-MM-DD es un formato válido para el backend
  }
  
  return dateString;
};

// Función para mostrar fecha en formato legible
const formatDateForDisplay = (dateString) => {
  if (!dateString) return '';
  
  try {
    // Si viene en formato YYYY-MM-DD
    if (dateString.includes('-') && dateString.split('-').length === 3) {
      const [year, month, day] = dateString.split('-');
      return `${day}/${month}/${year}`;
    }
    
    // Si viene como objeto Date o string de fecha
    const date = new Date(dateString);
    if (!isNaN(date.getTime())) {
      return date.toLocaleDateString('es-ES', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    }
    
    return dateString;
  } catch (error) {
    console.error('Error formatting date for display:', error);
    return dateString;
  }
};

// ===== COMPONENTE RESCHEDULE BUTTON CON RESTRICCIONES =====
export const RescheduleButton = ({ 
  appointment, 
  onRescheduleAppointment,
  size = "small",
  title = "Reschedule Appointment",
  showDisabledButton = true, // Mostrar botón deshabilitado cuando no se puede usar
  ...props 
}) => {
  const [open, setOpen] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [newDate, setNewDate] = useState('');
  const [newTime, setNewTime] = useState('');
  const [error, setError] = useState('');

  // Verificar si se puede hacer reschedule
  const canReschedule = canRescheduleAppointment(appointment);
  const currentStatus = appointment?.status || 'Unknown';

  // Función para manejar el reschedule
  const handleReschedule = async () => {
    // Verificar nuevamente que se puede hacer reschedule
    if (!canReschedule) {
      setError(`Cannot reschedule appointment with status: ${currentStatus}. Only 'Pending' and 'Reschedule' appointments can be rescheduled.`);
      return;
    }

    if (!newDate || !newTime) {
      setError('Please select both date and time for rescheduling.');
      return;
    }

    // Validar que la cita tenga los campos requeridos
    const validation = validateAppointmentForReschedule(appointment);
    if (!validation.isValid) {
      console.warn(`Appointment ${appointment.id} is missing fields:`, validation.missingFields);
      setError('Cannot reschedule appointment due to missing user data. Please check the appointment details.');
      return;
    }

    setIsProcessing(true);
    setError('');
    
    try {
      const appointmentService = new AppointmentService();
      
      // Usar _id o id dependiendo de lo que esté disponible
      const appointmentId = appointment._id || appointment.id;
      
      if (!appointmentId) {
        throw new Error('Appointment ID not found');
      }
      
      // Obtener los IDs de usuario con múltiples fallbacks
      const userid = appointment.userid || 
                    appointment.userId || 
                    appointment.patientId;
                    
      const meetinguserid = appointment.meetinguserid || 
                           appointment.meetingUserId || 
                           appointment.doctorId;
      
      // ✅ CORREGIR EL FORMATO DE FECHA - usar la función corregida
      const formattedDate = formatDateForBackend(newDate);
      
      if (!formattedDate) {
        throw new Error('Invalid date format');
      }
      
      // Preparar datos para el reschedule usando la estructura esperada por el service
      const rescheduleData = {
        date: formattedDate, // Usar fecha en formato correcto
        startTime: newTime,
        endTime: addOneHourToTime(newTime),
        userid: userid,
        meetinguserid: meetinguserid,
        // Preservar todos los demás datos de la cita
        patientName: appointment.patientName,
        doctorName: appointment.doctorName,
        appointmentType: appointment.appointmentType || 'consultation',
        status: 'Reschedule', // Cambiar el status a 'Reschedule' después de reagendar
        address: appointment.address || '',
        notes: appointment.notes || '',
        medicalHistory: appointment.medicalHistory || ''
      };
      
      console.log('🔄 Rescheduling appointment:', { 
        appointmentId, 
        originalDate: newDate, 
        formattedDate: formattedDate,
        rescheduleData 
      });
      
      // Usar el método rescheduleAppointment del service
      const result = await appointmentService.rescheduleAppointment(appointmentId, rescheduleData);
      
      if (!result) {
        throw new Error('No response received from server');
      }
      
      console.log('✅ Successfully rescheduled appointment:', result);
      
      // Ejecutar callback si existe
      if (onRescheduleAppointment) {
        onRescheduleAppointment(appointment, { newDate: formatDateForDisplay(newDate), newTime, result });
      }
      
      // Cerrar modal y limpiar campos
      setOpen(false);
      setNewDate('');
      setNewTime('');
      setError('');
      
      // Mostrar mensaje de éxito
      alert(`Appointment successfully rescheduled to ${formatDateForDisplay(newDate)} at ${newTime}`);
      
      // REFRESCAR LA PÁGINA para ver los cambios
      setTimeout(() => {
        window.location.reload();
      }, 1000); // Esperar 1 segundo para que el usuario vea el mensaje de éxito
      
    } catch (error) {
      console.error('❌ Error rescheduling appointment:', error);
      setError(`Failed to reschedule appointment: ${error.message}`);
    } finally {
      setIsProcessing(false);
    }
  };

  // Función para cerrar el modal
  const handleClose = () => {
    if (!isProcessing) {
      setOpen(false);
      setNewDate('');
      setNewTime('');
      setError('');
    }
  };

  // Función para abrir el modal
  const handleOpen = (e) => {
    e.stopPropagation();
    
    if (!canReschedule) {
      // Mostrar alerta si no se puede hacer reschedule
      alert(`Cannot reschedule appointment with status: ${currentStatus}. Only 'Pending' and 'Reschedule' appointments can be rescheduled.`);
      return;
    }
    
    setOpen(true);
    setError('');
  };

  // Si no se puede hacer reschedule y no se debe mostrar el botón deshabilitado
  if (!canReschedule && !showDisabledButton) {
    return null;
  }

  // Determinar el título del tooltip
  const tooltipTitle = canReschedule 
    ? title 
    : `Cannot reschedule - Status: ${currentStatus}. Only 'Pending' and 'Reschedule' appointments can be rescheduled.`;

  return (
    <>
      <Tooltip title={tooltipTitle} arrow>
        <span> {/* Wrapper span necesario para tooltip cuando el botón está deshabilitado */}
          <IconButton
            size={size}
            sx={{ 
              backgroundColor: canReschedule ? '#00AA00' : '#CCCCCC',
              color: canReschedule ? 'white' : '#666666',
              '&:hover': { 
                backgroundColor: canReschedule ? '#008800' : '#CCCCCC',
                cursor: canReschedule ? 'pointer' : 'not-allowed'
              },
              width: 24,
              height: 24
            }}
            onClick={handleOpen}
            disabled={!canReschedule}
            {...props}
          >
            {canReschedule ? (
              <RescheduleIcon sx={{ fontSize: 14 }} />
            ) : (
              <BlockIcon sx={{ fontSize: 14 }} />
            )}
          </IconButton>
        </span>
      </Tooltip>

      <Dialog 
        open={open} 
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <RescheduleIcon />
            <Typography variant="h6">Reschedule Appointment</Typography>
          </Box>
          <IconButton onClick={handleClose} disabled={isProcessing}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent>
          {/* Mostrar errores si los hay */}
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {/* Información de la cita */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Current Appointment:
            </Typography>
            <Box sx={{ 
              border: 1, 
              borderColor: 'divider', 
              borderRadius: 1, 
              p: 2,
              backgroundColor: 'grey.50'
            }}>
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>Patient:</strong> {appointment.patientName || 'Unknown Patient'}
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>Doctor:</strong> {appointment.doctorName || 'Not assigned'}
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>Current Date:</strong> {formatDateForDisplay(appointment.date) || 'No date'}
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>Current Time:</strong> {appointment.startTime || 'No time'} - {appointment.endTime || 'No end time'}
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>ID:</strong> {appointment._id || appointment.id || 'No ID'}
              </Typography>
              <Chip 
                label={appointment.status || 'scheduled'} 
                color={
                  appointment.status?.toLowerCase() === 'pending' ? 'warning' :
                  appointment.status?.toLowerCase() === 'reschedule' ? 'info' :
                  'default'
                }
                size="small"
              />
            </Box>
          </Box>

          {/* Campos de nueva fecha y hora */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="New Date"
              type="date"
              value={newDate}
              onChange={(e) => {
                const selectedDate = e.target.value;
                console.log('Selected date:', selectedDate); // Debug log
                setNewDate(selectedDate);
              }}
              InputLabelProps={{ shrink: true }}
              inputProps={{ min: new Date().toISOString().split('T')[0] }}
              disabled={isProcessing}
              fullWidth
              required
              helperText="Format: YYYY-MM-DD"
            />
            
            <TextField
              label="New Time"
              type="time"
              value={newTime}
              onChange={(e) => {
                const selectedTime = e.target.value;
                console.log('Selected time:', selectedTime); // Debug log
                setNewTime(selectedTime);
              }}
              InputLabelProps={{ shrink: true }}
              disabled={isProcessing}
              fullWidth
              required
              helperText="Format: HH:MM (24-hour)"
            />
          </Box>

          {/* Vista previa de la nueva fecha */}
          {newDate && (
            <Alert severity="info" sx={{ mt: 2 }}>
              <Typography variant="body2">
                <strong>Preview:</strong> New appointment date will be: {formatDateForDisplay(newDate)}
                {newTime && ` at ${newTime} - ${addOneHourToTime(newTime)}`}
              </Typography>
            </Alert>
          )}

          {/* Información adicional */}
          <Alert severity="info" sx={{ mt: 2 }}>
            <Typography variant="body2">
              <strong>Note:</strong> The end time will be automatically set to one hour after the start time.
              The appointment status will be changed to 'Reschedule' after rescheduling.
            </Typography>
          </Alert>
        </DialogContent>

        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button
            onClick={handleClose}
            disabled={isProcessing}
            variant="outlined"
            startIcon={<CloseIcon />}
          >
            Cancel
          </Button>
          <Button
            onClick={handleReschedule}
            disabled={isProcessing || !newDate || !newTime}
            variant="contained"
            startIcon={<SaveIcon />}
            sx={{ 
              backgroundColor: '#00AA00',
              '&:hover': { backgroundColor: '#008800' }
            }}
          >
            {isProcessing ? 'Rescheduling...' : 'Reschedule'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

// ===== COMPONENTE PARA RESCHEDULE MÚLTIPLE CON RESTRICCIONES CORREGIDO =====
export const BulkRescheduleButton = ({ 
  appointments = [], 
  onBulkReschedule,
  disabled = false 
}) => {
  const [open, setOpen] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [newDate, setNewDate] = useState('');
  const [newTime, setNewTime] = useState('');
  const [error, setError] = useState('');
  const [results, setResults] = useState([]);

  // Filtrar solo las citas que se pueden reagendar
  const rescheduleableAppointments = appointments.filter(canRescheduleAppointment);
  const nonRescheduleableAppointments = appointments.filter(app => !canRescheduleAppointment(app));

  const handleBulkReschedule = async () => {
    if (!newDate || !newTime) {
      setError('Please select both date and time for rescheduling.');
      return;
    }

    if (rescheduleableAppointments.length === 0) {
      setError('No appointments can be rescheduled. Only appointments with status "Pending" or "Reschedule" can be rescheduled.');
      return;
    }

    setIsProcessing(true);
    setError('');
    setResults([]);
    
    try {
      const appointmentService = new AppointmentService();
      const rescheduleResults = [];
      
      // ✅ CORREGIR EL FORMATO DE FECHA para bulk reschedule también
      const formattedDate = formatDateForBackend(newDate);
      
      if (!formattedDate) {
        throw new Error('Invalid date format');
      }
      
      // Procesar solo las citas que se pueden reagendar
      for (let i = 0; i < rescheduleableAppointments.length; i++) {
        const appointment = rescheduleableAppointments[i];
        
        try {
          const appointmentId = appointment._id || appointment.id;
          
          if (!appointmentId) {
            throw new Error('Appointment ID not found');
          }
          
          const userid = appointment.userid || 
                        appointment.userId || 
                        appointment.patientId;
                        
          const meetinguserid = appointment.meetinguserid || 
                               appointment.meetingUserId || 
                               appointment.doctorId;
          
          const rescheduleData = {
            date: formattedDate, // Usar fecha en formato correcto
            startTime: newTime,
            endTime: addOneHourToTime(newTime),
            userid: userid,
            meetinguserid: meetinguserid,
            patientName: appointment.patientName,
            doctorName: appointment.doctorName,
            appointmentType: appointment.appointmentType || 'consultation',
            status: 'Reschedule', // Cambiar status a 'Reschedule'
            address: appointment.address || '',
            notes: appointment.notes || '',
            medicalHistory: appointment.medicalHistory || ''
          };
          
          const result = await appointmentService.rescheduleAppointment(appointmentId, rescheduleData);
          rescheduleResults.push({
            appointment,
            result,
            success: true
          });
          
        } catch (error) {
          console.error(`Error rescheduling appointment ${appointment._id || appointment.id}:`, error);
          rescheduleResults.push({
            appointment,
            error: error.message,
            success: false
          });
        }
      }
      
      setResults(rescheduleResults);
      
      const successCount = rescheduleResults.filter(r => r.success).length;
      const failureCount = rescheduleResults.length - successCount;
      
      if (onBulkReschedule) {
        onBulkReschedule(rescheduleableAppointments, { newDate: formatDateForDisplay(newDate), newTime, results: rescheduleResults });
      }
      
      const skippedMessage = nonRescheduleableAppointments.length > 0 
        ? ` ${nonRescheduleableAppointments.length} appointments were skipped due to invalid status.`
        : '';
      
      if (failureCount === 0) {
        setOpen(false);
        setNewDate('');
        setNewTime('');
        alert(`Successfully rescheduled ${successCount} appointments to ${formatDateForDisplay(newDate)} at ${newTime}.${skippedMessage}`);
        
        // REFRESCAR LA PÁGINA para ver los cambios
        setTimeout(() => {
          window.location.reload();
        }, 1000);
      } else {
        setError(`Rescheduled ${successCount} appointments successfully. ${failureCount} failed.${skippedMessage}`);
      }
      
    } catch (error) {
      console.error('Error bulk rescheduling appointments:', error);
      setError(`An error occurred while rescheduling: ${error.message}`);
    } finally {
      setIsProcessing(false);
    }
  };

  // Determinar si el botón debe estar deshabilitado
  const isButtonDisabled = disabled || appointments.length === 0 || rescheduleableAppointments.length === 0;

  return (
    <>
      <Tooltip 
        title={
          rescheduleableAppointments.length === 0 && appointments.length > 0
            ? "No appointments can be rescheduled. Only 'Pending' and 'Reschedule' appointments are allowed."
            : `Reschedule ${rescheduleableAppointments.length} eligible appointments`
        }
        arrow
      >
        <span>
          <Button
            variant="contained"
            startIcon={<RescheduleIcon />}
            onClick={() => setOpen(true)}
            disabled={isButtonDisabled}
            sx={{ 
              backgroundColor: isButtonDisabled ? '#CCCCCC' : '#00AA00',
              '&:hover': { backgroundColor: isButtonDisabled ? '#CCCCCC' : '#008800' }
            }}
          >
            Reschedule ({rescheduleableAppointments.length})
            {nonRescheduleableAppointments.length > 0 && ` / ${appointments.length} total`}
          </Button>
        </span>
      </Tooltip>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <RescheduleIcon />
            <Typography variant="h6">
              Reschedule {rescheduleableAppointments.length} Appointments
            </Typography>
          </Box>
        </DialogTitle>

        <DialogContent>
          {/* Mostrar errores si los hay */}
          {error && (
            <Alert severity={results.length > 0 ? "warning" : "error"} sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {/* Mostrar advertencia si hay citas que no se pueden reagendar */}
          {nonRescheduleableAppointments.length > 0 && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              <Typography variant="body2">
                <strong>Warning:</strong> {nonRescheduleableAppointments.length} appointments cannot be rescheduled 
                because their status is not 'Pending' or 'Reschedule'. Only {rescheduleableAppointments.length} 
                appointments will be processed.
              </Typography>
            </Alert>
          )}

          {/* Mostrar resultados si los hay */}
          {results.length > 0 && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Reschedule Results:
              </Typography>
              <Box sx={{ 
                maxHeight: 150, 
                overflow: 'auto', 
                border: 1, 
                borderColor: 'divider', 
                borderRadius: 1, 
                p: 1 
              }}>
                {results.map((result, index) => (
                  <Box key={index} sx={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center', 
                    py: 0.5 
                  }}>
                    <Typography variant="body2">
                      {result.appointment.patientName || 'Unknown Patient'}
                    </Typography>
                    <Chip 
                      label={result.success ? 'Success' : 'Failed'} 
                      size="small" 
                      color={result.success ? 'success' : 'error'}
                    />
                  </Box>
                ))}
              </Box>
            </Box>
          )}

          {/* Sección de citas elegibles */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Appointments eligible for rescheduling ({rescheduleableAppointments.length}):
            </Typography>
            <Box sx={{ 
              maxHeight: 150, 
              overflow: 'auto', 
              border: 1, 
              borderColor: 'divider', 
              borderRadius: 1, 
              p: 1 
            }}>
              {rescheduleableAppointments.map((appointment, index) => (
                <Box key={appointment._id || appointment.id || index} sx={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center', 
                  py: 0.5 
                }}>
                  <Typography variant="body2">
                    {appointment.patientName || 'Unknown Patient'} - {formatDateForDisplay(appointment.date)} {appointment.startTime}
                  </Typography>
                  <Chip 
                    label={appointment.status || 'scheduled'} 
                    size="small" 
                    color={
                      appointment.status?.toLowerCase() === 'pending' ? 'warning' :
                      appointment.status?.toLowerCase() === 'reschedule' ? 'info' :
                      'default'
                    }
                  />
                </Box>
              ))}
            </Box>
          </Box>

          {/* Sección de citas no elegibles (si las hay) */}
          {nonRescheduleableAppointments.length > 0 && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Appointments NOT eligible for rescheduling ({nonRescheduleableAppointments.length}):
              </Typography>
              <Box sx={{ 
                maxHeight: 150, 
                overflow: 'auto', 
                border: 1, 
                borderColor: 'error.main', 
                borderRadius: 1, 
                p: 1,
                backgroundColor: 'error.light',
                opacity: 0.7
              }}>
                {nonRescheduleableAppointments.map((appointment, index) => (
                  <Box key={appointment._id || appointment.id || index} sx={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center', 
                    py: 0.5 
                  }}>
                    <Typography variant="body2" color="text.secondary">
                      {appointment.patientName || 'Unknown Patient'} - {formatDateForDisplay(appointment.date)} {appointment.startTime}
                    </Typography>
                    <Chip 
                      label={appointment.status || 'scheduled'} 
                      size="small" 
                      color="error"
                    />
                  </Box>
                ))}
              </Box>
            </Box>
          )}

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="New Date"
              type="date"
              value={newDate}
              onChange={(e) => {
                const selectedDate = e.target.value;
                console.log('Bulk reschedule - Selected date:', selectedDate); // Debug log
                setNewDate(selectedDate);
              }}
              InputLabelProps={{ shrink: true }}
              inputProps={{ min: new Date().toISOString().split('T')[0] }}
              disabled={isProcessing}
              fullWidth
              required
              helperText="Format: YYYY-MM-DD"
            />
            
            <TextField
              label="New Time"
              type="time"
              value={newTime}
              onChange={(e) => {
                const selectedTime = e.target.value;
                console.log('Bulk reschedule - Selected time:', selectedTime); // Debug log
                setNewTime(selectedTime);
              }}
              InputLabelProps={{ shrink: true }}
              disabled={isProcessing}
              fullWidth
              required
              helperText="Format: HH:MM (24-hour)"
            />
          </Box>

          {/* Vista previa de la nueva fecha para bulk reschedule */}
          {newDate && (
            <Alert severity="info" sx={{ mt: 2 }}>
              <Typography variant="body2">
                <strong>Preview:</strong> All eligible appointments will be rescheduled to: {formatDateForDisplay(newDate)}
                {newTime && ` at ${newTime} - ${addOneHourToTime(newTime)}`}
              </Typography>
            </Alert>
          )}

          <Alert severity="info" sx={{ mt: 2 }}>
            All eligible appointments will be rescheduled to the same date and time with status 'Reschedule'.
          </Alert>
        </DialogContent>

        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button onClick={() => setOpen(false)} disabled={isProcessing}>
            Cancel
          </Button>
          <Button
            onClick={handleBulkReschedule}
            disabled={isProcessing || !newDate || !newTime || rescheduleableAppointments.length === 0}
            variant="contained"
            startIcon={<SaveIcon />}
            sx={{ 
              backgroundColor: '#00AA00',
              '&:hover': { backgroundColor: '#008800' }
            }}
          >
            {isProcessing ? 'Processing...' : `Reschedule ${rescheduleableAppointments.length} Appointments`}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default RescheduleButton;